/**
 * Where the room chat sits, and whether it is showing.
 *
 * *** THE STUDENT'S CHOICE, NOT A BUILD-TIME ONE ***
 * Both placements are right for different people. Someone revising alone
 * wants the room's full width for tiles and the chat out of the way;
 * someone working through a doubt wants the conversation in the column
 * where it cannot be forgotten. Neither is the correct default for both,
 * so it is a preference.
 *
 * *** THREE STATES, NOT TWO ***
 * Docked, pinned, hidden — and hidden is reachable only from docked. A
 * pinned chat is part of the column; collapsing it there would leave an
 * empty stub above the duty strip with nothing in it.
 */

import { useCallback, useEffect, useState } from 'react'

export type ChatPlacement = 'dock' | 'pin'

/**
 * Per viewer, per browser.
 *
 * `localStorage` is right here precisely because this is a convenience:
 * it survives a reload, and if it comes back empty — a private window,
 * cleared site data — the room still works, it just opens docked. Nothing
 * about a student's study time or entitlement lives here.
 */
const PLACEMENT_KEY = 'library.chat.placement'
const HIDDEN_KEY = 'library.chat.hidden'

/** Docked, because it costs the tile grid nothing until someone opens it. */
const DEFAULT_PLACEMENT: ChatPlacement = 'dock'

function read(key: string, fallback: string): string {
  try {
    return window.localStorage.getItem(key) ?? fallback
  } catch {
    // Private windows and blocked site data both throw on access. The
    // preference is a convenience; losing it is not worth a broken room.
    return fallback
  }
}

function write(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value)
  } catch {
    // Same reason. The choice still applies for this visit.
  }
}

export function useChatPlacement() {
  const [placement, setPlacementState] = useState<ChatPlacement>(() =>
    read(PLACEMENT_KEY, DEFAULT_PLACEMENT) === 'pin' ? 'pin' : 'dock',
  )
  const [hidden, setHiddenState] = useState<boolean>(
    () => read(HIDDEN_KEY, 'false') === 'true',
  )

  const setPlacement = useCallback((next: ChatPlacement) => {
    setPlacementState(next)
    write(PLACEMENT_KEY, next)
    // Moving the chat always reveals it: a student who just pinned it and
    // saw nothing would reasonably think the button was broken.
    setHiddenState(false)
    write(HIDDEN_KEY, 'false')
  }, [])

  const setHidden = useCallback((next: boolean) => {
    setHiddenState(next)
    write(HIDDEN_KEY, String(next))
  }, [])

  /*
    *** A PINNED CHAT CANNOT BE HIDDEN ***
    Enforced here rather than in the component, so no caller can produce
    the state where the column holds an invisible panel.
  */
  useEffect(() => {
    if (placement === 'pin' && hidden) setHidden(false)
  }, [placement, hidden, setHidden])

  return {
    placement,
    hidden: placement === 'dock' && hidden,
    setPlacement,
    setHidden,
  }
}
